import { useCallback, useState } from 'react';
import { useMutation } from 'react-relay';
import type { PayloadError } from 'relay-runtime';
import { useToast } from '@pv-frontend/ui-components-web';
import { defineExperimentMutation, enrollExperimentCohortMutation } from './experiment-mutations';
import { featureErrorCode, humanizeFeatureCode } from './feature-flag-errors';

/**
 * The mutations are untyped by Relay (no generated response types are wired in),
 * so responses arrive as a loose bag keyed by the root field name.
 */
type MutationResponse = Record<string, unknown>;

/** A single weighted arm to define. */
export interface VariantInput {
    name: string;
    weight: number;
}

/** The payload the define form hands over. */
export interface DefineExperimentInput {
    key: string;
    flagKey: string;
    variants: readonly VariantInput[];
}

export interface ExperimentActions {
    /** True while a `defineExperiment` mutation is in flight. */
    defining: boolean;
    /** True while an `enrollExperimentCohort` mutation is in flight. */
    enrolling: boolean;
    /** Define an experiment; `onSuccess` fires only on a clean commit (e.g. to reset the form). */
    onDefine: (input: DefineExperimentInput, onSuccess?: () => void) => void;
    /** Enroll a vault cohort; `onSuccess` fires only on a clean commit (e.g. to clear the selection). */
    onEnroll: (experimentKey: string, vaultIds: readonly string[], onSuccess?: () => void) => void;
}

/** Build an error toast, preferring a surfaced `FEATURE_*` code over a generic fallback. */
function errorToast(source: unknown, fallbackTitle: string): { title: string; description: string } {
    const code = featureErrorCode(source);
    if (code) return { title: humanizeFeatureCode(code), description: code };
    return { title: fallbackTitle, description: 'The change was rejected. Please try again.' };
}

/**
 * Drives the two `Apis.Admin` experiment write actions — define an experiment and
 * enroll a vault cohort — with success/error toasts and a caller-supplied refresh
 * (the `experiments` root list can't be reconciled from the mutation payloads, so
 * the screen refetches on success). Neither action is optimistic: an experiment's
 * identity and a cohort's membership are server-authoritative, so we wait for the
 * commit before reflecting them.
 */
export function useExperimentActions(onRefresh: () => void): ExperimentActions {
    const toast = useToast();
    const [defining, setDefining] = useState(false);
    const [enrolling, setEnrolling] = useState(false);

    const [commitDefine] = useMutation(defineExperimentMutation);
    const [commitEnroll] = useMutation(enrollExperimentCohortMutation);

    const onDefine = useCallback(
        (input: DefineExperimentInput, onSuccess?: () => void) => {
            setDefining(true);
            commitDefine({
                variables: {
                    key: input.key,
                    flagKey: input.flagKey,
                    variants: input.variants.map((v) => ({ name: v.name, weight: v.weight })),
                },
                onCompleted: (_response: MutationResponse, errors: PayloadError[] | null) => {
                    setDefining(false);
                    if (errors && errors.length > 0) {
                        const { title, description } = errorToast(errors, "Couldn't define the experiment");
                        toast.error(title, description);
                        return;
                    }
                    toast.success('Experiment defined', input.key);
                    onRefresh();
                    onSuccess?.();
                },
                onError: (error: Error) => {
                    setDefining(false);
                    const { title, description } = errorToast(error, "Couldn't define the experiment");
                    toast.error(title, description);
                },
            });
        },
        [commitDefine, onRefresh, toast],
    );

    const onEnroll = useCallback(
        (experimentKey: string, vaultIds: readonly string[], onSuccess?: () => void) => {
            setEnrolling(true);
            commitEnroll({
                variables: { experimentKey, vaultIds: [...vaultIds] },
                onCompleted: (response: MutationResponse, errors: PayloadError[] | null) => {
                    setEnrolling(false);
                    if (errors && errors.length > 0) {
                        const { title, description } = errorToast(errors, "Couldn't enroll the cohort");
                        toast.error(title, description);
                        return;
                    }
                    const enrolled = typeof response.enrollExperimentCohort === 'number'
                        ? response.enrollExperimentCohort
                        : vaultIds.length;
                    const noun = enrolled === 1 ? 'vault' : 'vaults';
                    toast.success(
                        enrolled === 0 ? 'No new vaults enrolled' : `Enrolled ${enrolled} ${noun}`,
                        experimentKey,
                    );
                    onRefresh();
                    onSuccess?.();
                },
                onError: (error: Error) => {
                    setEnrolling(false);
                    const { title, description } = errorToast(error, "Couldn't enroll the cohort");
                    toast.error(title, description);
                },
            });
        },
        [commitEnroll, onRefresh, toast],
    );

    return { defining, enrolling, onDefine, onEnroll };
}
